import { Box, Tab, Tabs, IconButton } from "@mui/material";
import { useContext, useEffect, useReducer, useState } from "react";
import { X } from "lucide-react";

import ModalSelectAuxNurseBody from "./modalSelectAuxNurse/modalSelectAuxNurseBody";
import { PatientsListContext } from "../../components/PatientsList/PatientsList";
import ModalSelectNurseBody from "./modalSelectNurseBody/modalSelectNurseBody";
import CustomSnackbar, { SnackbarModes } from "../Snackbars/Snackbar/Snackbar";
import ModalCategoryBody from "./modalCategoryBody/modalCategoryBody";
import ModalNTISSBody from "./modalNTISS/modalNTISSBody";
import { getHeaders, URI_API } from "../../utils/config";
import ModalTISSBody from "./modalTISS/modalTISSBody";
import "./assignNurseModal.scss";
import { categoryReducer } from "./categoryReducer";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<Box
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box className="modal-tab-content">{children}</Box>}
		</Box>
	);
}

interface AssignNurseModalBodyProps {
	closeModal: () => void;
}

export default function AssignNurseModalBody({ closeModal }: AssignNurseModalBodyProps) {
	const [value, setValue] = useState(0);
	const {
		tissOptions,
		ntissOptions,
		selectedPatient,
		categoryList,
		setOpenAssignNurseModal,
		setConfirmationTitle,
		openNurseCategoryAlert,
		setOpenNurseCategoryAlert,
		setOpenConfirmationModal,
		updatePatients,
	} = useContext(PatientsListContext);
	const [isEdit, setIsEdit] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [activeItems, setActiveItems] = useState([]);
	const [categoryState, dispatchCategory] = useReducer(categoryReducer, {
		selectedOptionsCategory: [],
		count: 0,
	});

	const tissRooms = ["519", "520", "521", "522", "523", "524", "525", "526"]

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const handleTab = () => {
		setValue(value + 1);
	};

	const categoryTab = () => {
		if (tissOptions.includes(selectedPatient?.areaname) 
			|| ( 
				tissRooms.includes(selectedPatient?.room)
				&& selectedPatient.hospital_name === 'Alta Especialidad' 
				)
			) {
			return (
				<ModalTISSBody
					handleTab={handleTab}
					setIsEdit={setIsEdit}
					activeItems={activeItems}
					categoryState={categoryState}
					dispatchCategory={dispatchCategory}
				/>
			);
		} else if (ntissOptions.includes(selectedPatient?.areaname)) {
			return (
				<ModalNTISSBody
					handleTab={handleTab}
					setIsEdit={setIsEdit}
					activeItems={activeItems}
					categoryState={categoryState}
					dispatchCategory={dispatchCategory}
				/>
			);
		} else {
			return (
				<ModalCategoryBody
					handleTab={handleTab}
					setIsEdit={setIsEdit}
					activeItems={activeItems}
					categoryState={categoryState}
					dispatchCategory={dispatchCategory}
				/>
			);
		}
	};

	useEffect(() => {
		console.log("Selected Patient:", selectedPatient);
		fetch(`${URI_API}/checklist_active_items/${selectedPatient?.id}`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				setActiveItems(body);
			});
	}, []);

	const savePatient = (updatedPatient: any) => {
		setIsLoading(true);
		let data = {};
		data = {
			area_id: updatedPatient.area_id,
			areaname: updatedPatient.areaname,
			categoryid: updatedPatient.categoryid,
			categoryname: updatedPatient.categoryname,
			date_of_admission: updatedPatient.date_of_admission,
			patient_number: updatedPatient.patient_number,
			id: updatedPatient.id,
			name: updatedPatient.name,
			ntissvalue: updatedPatient.ntiss_value || "0",
			nurse_id: updatedPatient.nurse_id,
			nursename: updatedPatient.nursename,
			room: updatedPatient.room,
			checklist: categoryList.map((item: any) => ({
				id: item.id,
				checked: item.checked,
			})),
			secondaryNursesid: updatedPatient.secondary_nurse_id,
			secondarynursename: updatedPatient.secondarynursename,
			tissvalue: updatedPatient.tiss_value || "0",
		};
		fetch(`${URI_API}/patients`, getHeaders("PUT", data))
			.then(async (res) => {
				const body = await res.json();
				if (res.status === 200) {
					if (isEdit === true) {
						setConfirmationTitle("Actualización exitosa");
					} else {
						setConfirmationTitle("Paciente asignado");
					}
					setIsLoading(false);
					setOpenConfirmationModal(true);
					updatePatients();
				} else {
					setConfirmationTitle("Algo salió mal");
					setIsLoading(false);
					setOpenConfirmationModal(true);
				}
				setOpenAssignNurseModal(false);
			})
			.catch((e) => {
				setConfirmationTitle("Algo salió mal");
				setOpenConfirmationModal(true);
			});
	};

	return (
		<>
			<Box
				className="modal-card"
				id="onboarding-6"
			>
				<Box className="modal-card__navigation-tab">
					<Box>
						<Tabs
							onChange={handleChange}
							value={value}
						>
							<Tab
								sx={{
									paddingRight: "0px !important",
								}}
								label="Categoría"
								value={0}
								className="modal-card__navigation-tab__tabs"
								disableRipple
							/>
							<Tab
								sx={{
									paddingLeft: "0px !important",
									paddingRight: "0px !important",
								}}
								label="Profesional"
								value={1}
								className="modal-card__navigation-tab__tabs"
								disableRipple
								disabled={value < 1}
							/>
							<Tab
								sx={{
									paddingLeft: "0px !important",
									paddingRight: "0px !important",
								}}
								value={2}
								className="modal-card__navigation-tab__tabs"
								label="Profesional Auxiliar"
								disableRipple
								disabled={value < 2}
							/>
						</Tabs>
					</Box>
					<Box className="modal-card__navigation-tab__close-icon-container">
						<IconButton
							aria-label="close"
							onClick={closeModal}
							disableRipple
						>
							<X className="modal-card__navigation-tab__close-icon-container__icon"></X>
						</IconButton>
					</Box>
				</Box>
				<CustomTabPanel
					value={value}
					index={0}
				>
					{categoryTab()}
				</CustomTabPanel>
				<CustomTabPanel
					value={value}
					index={1}
				>
					<ModalSelectNurseBody handleTab={handleTab} />
				</CustomTabPanel>
				<CustomTabPanel
					value={value}
					index={2}
				>
					<ModalSelectAuxNurseBody
						savePatient={savePatient}
						isLoading={isLoading}
						setIsLoading={setIsLoading}
					/>
				</CustomTabPanel>
			</Box>
			<CustomSnackbar
				open={openNurseCategoryAlert}
				onClose={() => setOpenNurseCategoryAlert(false)}
				title="Recomendación de Asignación de Profesional"
				subtitle="Para garantizar una atención adecuada, se sugiere asignar a este paciente un profesional de categoría A."
				mode={SnackbarModes.CATEGORY}
			/>
		</>
	);
}
